<template>
  <div class="subrangeListComponent">
    <b-card no-body>
      <b-card-header class="d-flex align-items-center justify-content-between">
        Actions
        <b-dropdown variant="light" size="sm" no-caret>
          <template v-slot:button-content>
            <fa icon="bars" />
          </template>

          <b-dropdown-group header="Labels">
            <b-dropdown-item
              v-for="option in actionSetOptions"
              :key="option.id"
              @click="() => $emit('changeActionSet', option.id)"
            >
              {{ option.label }}
            </b-dropdown-item>
          </b-dropdown-group>

          <b-dropdown-divider />

          <b-dropdown-group header="Colors">
            <b-dropdown-item
              v-for="option in colorSchemeOptions"
              :key="option.id"
              @click="() => $emit('changeColorScheme', option.id)"
            >
              {{ option.label }}
            </b-dropdown-item>
          </b-dropdown-group>

          <b-dropdown-divider />

          <b-dropdown-item @click="() => $emit('clearGrid')">
            Clear Grid
          </b-dropdown-item>
        </b-dropdown>
      </b-card-header>

      <b-card-body>
        <b-list-group>
          <div v-for="subrange of subrangeData" :key="subrange.index">
            <div v-if="subrange.action !== null" class="d-flex">
              <b-list-group-item
                :class="{ active: subrange.isActive, dark: subrange.isDark }"
                class="subrangeListItem"
                :style="subrange.style"
                @click="$emit('activateSubrange', subrange.index)"
              >
                {{ subrange.label }}
                <div class="color" :style="subrange.colorBarStyle" />
              </b-list-group-item>
              <div class="comboCounts" v-if="subrange.combos > 0">
                <div>{{ subrange.comboInRangePercent }}% ({{ subrange.comboPercent }}%)</div>
                <div>{{ subrange.combos }}/{{ subrange.totalCombosInRange }}</div>
              </div>
            </div>
          </div>

          <div class="d-flex mt-2" v-if="hasCombosNotInRange">
            <b-list-group-item class="subrangeListItem notInRange">
              {{ notInRangeData.label }}
              <div class="color" />
            </b-list-group-item>
            <div class="comboCounts" v-if="notInRangeData.combos > 0">
              <div>{{ notInRangeData.comboPercent }}%</div>
              <div>{{ notInRangeData.combos }}/{{ notInRangeData.totalCombos }}</div>
            </div>
          </div>
        </b-list-group>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import { fractionToPercent } from "../helpers/number";
import { darken, isDark } from "../helpers/color";

export default {
  name: "SubrangeList",
  props: {
    subranges: { type: Array, required: true },
    colorScheme: { type: Array, required: true },
    activeSubrangeIndex: { type: Number, default: 0 },
    comboCounts: { type: Object, required: true },
    actionSetOptions: { type: Array, required: true },
    colorSchemeOptions: { type: Array, required: true },
  },

  data() {
    return {};
  },

  computed: {
    subrangeData() {
      return this.subranges.map(this.subrangeToData);
    },
    notInRangeData() {
      return this.subrangeToData({ label: "Not In Range" }, -1);
    },
    hasCombosNotInRange() {
      return this.comboCounts.bySubrangeIndex[-1] > 0;
    },
  },

  methods: {
    subrangeToData(subrange, i) {
      const isActive = i === this.activeSubrangeIndex;
      const color = this.colorScheme[i] || "#ffffff";
      const backgroundIsDark = isDark(color);
      const darkColor = darken(color);
      const colorBarStyle = {
        backgroundColor: color,
        borderColor: darkColor,
      };
      const style = isActive ? colorBarStyle : {};
      const totalCombos = this.comboCounts.total;
      const totalCombosInRange = this.comboCounts.totalInRange;
      const combos = this.comboCounts.bySubrangeIndex[i] || 0;
      const comboFraction = totalCombos === 0 ? 0 : combos / totalCombos;
      const comboInRangeFraction = totalCombosInRange === 0 ? 0 : combos / totalCombosInRange;
      return {
        ...subrange,
        index: i,
        isActive,
        isDark: backgroundIsDark,
        color,
        style,
        colorBarStyle,
        totalCombos,
        totalCombosInRange,
        combos,
        comboPercent: fractionToPercent(comboFraction, 0),
        comboInRangePercent: fractionToPercent(comboInRangeFraction, 0),
      };
    },
  },
};
</script>

<style lang="scss" scoped>
.subrangeListItem {
  position: relative;
  top: 0;
  left: 0;
  cursor: pointer;
  transition: all 0.1s;
  color: black;
  width: 230px;
  padding: 0.61rem 1.25rem;
  &.active {
    top: -2px;
    left: -2px;
    box-shadow: 3px 3px 3px 0px rgba(0, 0, 0, 1);
    color: black;
    &.dark {
      color: white;
    }
  }
  &.notInRange {
    cursor: default;
  }
  .color {
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: 10px;
  }
}
.comboCounts {
  flex: 1;
  text-align: right;
  font-size: 10px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}
</style>
